import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "rr_pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    // iOS
    (window.navigator as unknown as { standalone?: boolean }).standalone === true);

const isIos = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios/i.test(navigator.userAgent);

const recentlyDismissed = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const days = (Date.now() - Number(v)) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
};

const InstallPwaPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      trackEvent("pwa_launched", { mode: "standalone" }).catch(() => {});
      return;
    }
    if (recentlyDismissed()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      trackEvent("pwa_installed", {}).catch(() => {});
      setShow(false);
    };
    window.addEventListener("appinstalled", installedHandler);

    // iOS doesn't fire beforeinstallprompt — show a hint after a delay
    if (isIos()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setShow(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installedHandler);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setShow(false);
    trackEvent("pwa_install_dismissed", { ios: iosHint }).catch(() => {});
  };

  const install = async () => {
    if (!deferred) return;
    trackEvent("pwa_install_prompt_shown", {}).catch(() => {});
    await deferred.prompt();
    const result = await deferred.userChoice;
    trackEvent("pwa_install_choice", { outcome: result.outcome }).catch(() => {});
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:max-w-sm">
      <div className="glass rounded-2xl border border-primary/30 p-4 shadow-2xl backdrop-blur-md bg-background/90">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-sm">
              Install Romance Radar 💖
            </p>
            <p className="text-muted-foreground text-xs mt-1">
              {iosHint
                ? "Tap Share then Add to Home Screen for the best mobile experience."
                : "Install for a faster, app-like mobile experience ✨"}
            </p>
            {!iosHint && (
              <button
                onClick={install}
                className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                Add to Home Screen
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPwaPrompt;
